import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import type { Notification, SystemAlert, NotificationContextType } from '../types/notification';

interface NotificationState {
  notifications: Notification[];
  systemAlerts: SystemAlert[];
}

type NotificationAction =
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'REMOVE_NOTIFICATION'; payload: string }
  | { type: 'CLEAR_ALL_NOTIFICATIONS' }
  | { type: 'ADD_SYSTEM_ALERT'; payload: SystemAlert }
  | { type: 'REMOVE_SYSTEM_ALERT'; payload: string };

const initialState: NotificationState = {
  notifications: [],
  systemAlerts: [],
};

const notificationReducer = (state: NotificationState, action: NotificationAction): NotificationState => {
  switch (action.type) {
    case 'ADD_NOTIFICATION':
      // Limit to 5 notifications to prevent overwhelming users
      const newNotifications = [action.payload, ...state.notifications].slice(0, 5);
      return {
        ...state,
        notifications: newNotifications,
      };
    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload),
      };
    case 'CLEAR_ALL_NOTIFICATIONS':
      return {
        ...state,
        notifications: [],
      };
    case 'ADD_SYSTEM_ALERT':
      return {
        ...state,
        systemAlerts: [action.payload, ...state.systemAlerts],
      };
    case 'REMOVE_SYSTEM_ALERT':
      return {
        ...state,
        systemAlerts: state.systemAlerts.filter(a => a.id !== action.payload),
      };
    default:
      return state;
  }
};

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'createdAt'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newNotification: Notification = {
      ...notification,
      id,
      createdAt: new Date(),
      duration: notification.duration ?? (notification.type === 'error' ? 0 : 5000), // Errors persist until dismissed
    };

    dispatch({ type: 'ADD_NOTIFICATION', payload: newNotification });

    // Auto-remove notification after duration (if not persistent)
    if (newNotification.duration && newNotification.duration > 0 && !newNotification.persistent) {
      setTimeout(() => {
        dispatch({ type: 'REMOVE_NOTIFICATION', payload: id });
      }, newNotification.duration);
    }
  }, []);

  const removeNotification = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_NOTIFICATION', payload: id });
  }, []);

  const clearAllNotifications = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_NOTIFICATIONS' });
  }, []);

  const addSystemAlert = useCallback((alert: Omit<SystemAlert, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newAlert: SystemAlert = {
      ...alert,
      id,
    };

    dispatch({ type: 'ADD_SYSTEM_ALERT', payload: newAlert });
  }, []);

  const removeSystemAlert = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_SYSTEM_ALERT', payload: id });
  }, []);

  // Auto-remove expired system alerts
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      state.systemAlerts.forEach(alert => {
        if (alert.endTime && alert.endTime < now) {
          removeSystemAlert(alert.id);
        }
      });
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [state.systemAlerts, removeSystemAlert]);

  const value: NotificationContextType = {
    notifications: state.notifications,
    systemAlerts: state.systemAlerts,
    addNotification,
    removeNotification,
    clearAllNotifications,
    addSystemAlert,
    removeSystemAlert,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};